import { MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES } from '@holdthat/core';

export interface CaptureDraft { value: string; title: string; files: File[] }

export function hasCaptureData(transfer: Pick<DataTransfer, 'types'>) {
  return [...transfer.types].some((type) => ['Files', 'text/uri-list', 'text/plain', 'text/x-moz-url'].includes(type));
}

export function validateCaptureFiles(files: File[]) {
  if (files.length > MAX_ATTACHMENTS) throw new Error(`Pro Hold sind höchstens ${MAX_ATTACHMENTS} Dateien möglich. Bitte eine kleinere Auswahl ablegen.`);
  if (files.some((file) => file.size > MAX_ATTACHMENT_BYTES)) throw new Error('Eine Datei ist größer als 100 MB. Es wurde nichts übernommen.');
}

function webURL(value: string) {
  if (/\s/.test(value)) return undefined;
  try { const url = new URL(value.startsWith('www.') ? `https://${value}` : value); return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined; }
  catch { return undefined; }
}

/** Read the transfer synchronously during drop; the browser clears it afterwards. */
export function readCaptureDrop(transfer: DataTransfer): CaptureDraft {
  const items = [...(transfer.items ?? [])];
  if (items.some((item) => item.kind === 'file' && item.webkitGetAsEntry?.()?.isDirectory)) throw new Error('Bitte einzelne Dateien auswählen. Ordner werden nicht automatisch durchsucht.');
  const files = [...transfer.files];
  if (items.some((item) => item.kind === 'file') && !files.length) throw new Error('Die abgelegten Dateien sind nicht lesbar. Bitte über „Dateien auswählen“ erneut wählen.');
  validateCaptureFiles(files);
  const uriList = transfer.getData('text/uri-list').split(/\r?\n/).map((line) => line.trim()).filter((line) => line && !line.startsWith('#'));
  const [mozillaURL = '', mozillaTitle = ''] = transfer.getData('text/x-moz-url').split(/\r?\n/);
  const plain = transfer.getData('text/plain').trim();
  const advertised = uriList.length ? uriList : mozillaURL ? [mozillaURL.trim()] : [];
  const urls = [...new Set(advertised.map(webURL).filter((url): url is string => Boolean(url)))];
  if (urls.length > 1) throw new Error('Bitte einen Link pro Hold ablegen. Die Auswahl wurde nicht gekürzt.');
  if (!files.length && advertised.length && (urls.length !== 1 || advertised.some((url) => !webURL(url)))) throw new Error('Dieser Link kann nicht übernommen werden. Unterstützt werden HTTP- und HTTPS-Links.');
  const plainURL = webURL(plain);
  const unsafePlainURL = /^(?:javascript|vbscript|data|blob|file):/i.test(plain) || Boolean(files.length && /^(?:[A-Za-z]:\\|\\\\|\/)/.test(plain));
  const value = urls[0] ?? plainURL ?? (unsafePlainURL ? '' : plain);
  if (!files.length && !value) throw new Error('Bitte Fotos, Dateien, einen Weblink oder Text ablegen. Browserbilder ohne Dateifreigabe können als Weblink gespeichert werden.');
  // Files dragged from the desktop sometimes carry file:// paths; never save those paths.
  const title = urls.length && mozillaTitle ? mozillaTitle.trim() : urls.length && plain && !plainURL && !unsafePlainURL && !/\r|\n/.test(plain) ? plain : '';
  return { value, title, files };
}
